import { supabase } from './supabase'

const STAFF_EMBED = `activity_staff(id, user_id, role, user:user_profiles(id, first_name, last_name, preferred_name))`

// Get a single activity by ID
export async function getActivity(activityId) {
  const { data, error } = await supabase
    .from('activities')
    .select(`*, ${STAFF_EMBED}`)
    .eq('id', activityId)
    .single()

  if (error) throw error
  return data
}

// Get all activities for an organization, with optional filters
export async function getActivities(organizationId, { termId, isActive = true } = {}) {
  let query = supabase
    .from('activities')
    .select(`
      *,
      ${STAFF_EMBED},
      activity_terms(id, term_id, is_primary, term:academic_terms(id, name, start_date, end_date)),
      calendar:calendars(id, name, color)
    `)
    .eq('organization_id', organizationId)
    .eq('is_active', isActive)
    .order('block', { ascending: true, nullsFirst: false })
    .order('name')

  const { data, error } = await query
  if (error) throw error

  // Client-side term filter (PostgREST filters nested arrays but still returns all rows)
  if (termId) {
    return data.filter((a) => a.activity_terms?.some((at) => at.term_id === termId))
  }

  return data
}

// Get activities that need scheduling (admin view)
export async function getNeedsScheduling(organizationId) {
  const { data, error } = await supabase
    .from('activities')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('is_active', true)
    .eq('is_not_scheduled', false)
    .eq('is_release', false)
    .or('days_of_week.is.null,default_start_time.is.null')
    .order('created_at')

  if (error) throw error
  return data
}

// Create a new activity
export async function createActivity(activity) {
  const { data, error } = await supabase
    .from('activities')
    .insert(activity)
    .select()
    .single()

  if (error) throw error
  return data
}

// Delete an activity (hard delete — all FKs cascade)
export async function deleteActivity(activityId) {
  const { error } = await supabase
    .from('activities')
    .delete()
    .eq('id', activityId)

  if (error) throw error
}

// Update an activity
export async function updateActivity(activityId, updates) {
  const { data, error } = await supabase
    .from('activities')
    .update(updates)
    .eq('id', activityId)
    .select()
    .single()

  if (error) throw error
  return data
}

// Get activities for a teacher via the junction table (inner join filters to assigned activities).
// NOTE: confirmed dead code as of #70 — no callers found. Retained but not used.
export async function getTeacherActivities(teacherId, organizationId) {
  const { data: staffRows, error: staffError } = await supabase
    .from('activity_staff')
    .select('activity_id')
    .eq('user_id', teacherId)

  if (staffError) throw staffError

  const activityIds = staffRows.map((r) => r.activity_id)
  if (activityIds.length === 0) return []

  const { data, error } = await supabase
    .from('activities')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('is_active', true)
    .in('id', activityIds)
    .order('block')

  if (error) throw error
  return data
}

// Get a student's schedule — all activities they're enrolled in
export async function getStudentActivities(studentId) {
  const { data, error } = await supabase
    .from('activities')
    .select(`
      *,
      ${STAFF_EMBED},
      enrollments!inner(id, student_id, block, is_active, notes)
    `)
    .eq('enrollments.student_id', studentId)
    .eq('enrollments.is_active', true)
    .eq('is_active', true)
    .order('block', { ascending: true, nullsFirst: false })

  if (error) throw error
  return data
}

// Bulk update scalar fields (block, times, flags) across multiple activities
export async function bulkUpdateActivityFields(ids, updates) {
  const { data, error } = await supabase
    .from('activities')
    .update(updates)
    .in('id', ids)
    .select()

  if (error) throw error
  return data
}

// Sync the teacher+monitor rows for an activity to exactly match staffEntries.
// Diffs against current DB state: deletes stale rows, upserts new/changed ones.
// Only manages teacher/monitor roles; any future roles are untouched.
export async function setActivityStaff(activityId, staffEntries) {
  const { data: current, error: fetchError } = await supabase
    .from('activity_staff')
    .select('user_id, role')
    .eq('activity_id', activityId)
    .in('role', ['teacher', 'monitor'])

  if (fetchError) throw fetchError

  const newUserIds = new Set(staffEntries.map((e) => e.user_id).filter(Boolean))
  const toDelete = (current ?? []).filter((r) => !newUserIds.has(r.user_id))

  if (toDelete.length > 0) {
    const { error } = await supabase
      .from('activity_staff')
      .delete()
      .eq('activity_id', activityId)
      .in('user_id', toDelete.map((r) => r.user_id))
    if (error) throw error
  }

  const toUpsert = staffEntries
    .filter((e) => e.user_id)
    .map((e) => ({ activity_id: activityId, user_id: e.user_id, role: e.role }))

  if (toUpsert.length > 0) {
    const { error } = await supabase
      .from('activity_staff')
      .upsert(toUpsert, { onConflict: 'activity_id,user_id' })
    if (error) throw error
  }
}

// Get internship opportunities for an organization
export async function getInternshipOpportunities(organizationId, { isAvailable = true } = {}) {
  let query = supabase
    .from('internship_opportunities')
    .select('*')
    .eq('organization_id', organizationId)
    .order('name')

  if (isAvailable !== null) {
    query = query.eq('is_available', isAvailable)
  }

  const { data, error } = await query
  if (error) throw error
  return data
}
